/**
 * OmegaMarketResearchPage — all product/market research in one founder click.
 *
 * Founder-only. Fetches the five compile-time-baked research docs from
 * `GET /admin/market-research` and renders them with a left doc-switcher so the
 * Founder can jump between Monitor V2 / Loop V2 / V1 Monitor / V1 Loop / Proof
 * without ever asking for the data again. No new deps — markdown is rendered by
 * a small, safe in-file renderer (headings / bold / tables / bullets /
 * paragraphs). Matches the OMEGA white-theme page shell (see OmegaDlmPage).
 */

import { useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';

// ── Types ────────────────────────────────────────────────────────────────────

interface ResearchDoc {
  key: string;
  title: string;
  markdown: string;
}

interface ResearchResponse {
  docs: ResearchDoc[];
}

// ── Data ─────────────────────────────────────────────────────────────────────

function useResearch() {
  return useQuery({
    queryKey: ['market-research'],
    queryFn: () => apiFetch<ResearchResponse>('/admin/market-research'),
  });
}

// ── Minimal, safe markdown renderer ──────────────────────────────────────────
//
// Handles the subset the docs use: ATX headings (#/##/###), inline bold
// (**x**), pipe tables, bullet lists (- ), and paragraphs. No raw HTML is ever
// injected — every text run is escaped first, then only our own <strong> tags
// are re-introduced, so this cannot render attacker markup (the docs are
// trusted compile-time content, but we keep it safe regardless).

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Escape, then turn `**bold**` into <strong>. Returns sanitized HTML. */
export function renderInline(text: string): string {
  const escaped = escapeHtml(text);
  return escaped.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
}

/** Split a markdown table row into trimmed cells. */
function splitRow(line: string): string[] {
  return line
    .replace(/^\s*\|/, '')
    .replace(/\|\s*$/, '')
    .split('|')
    .map((c) => c.trim());
}

/** A table separator row like `| --- | :---: |`. */
function isTableSeparator(line: string): boolean {
  return /^\s*\|?[\s:|-]+\|?\s*$/.test(line) && line.includes('-');
}

type Block =
  | { kind: 'h'; level: number; text: string }
  | { kind: 'p'; text: string }
  | { kind: 'ul'; items: string[] }
  | { kind: 'table'; header: string[]; rows: string[][] };

/** Parse trusted markdown into a small block list (no raw HTML passthrough). */
export function parseMarkdown(md: string): Block[] {
  const lines = md.replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];
  let i = 0;

  /** Is line `n` a table header (pipe row followed by a separator row)? */
  const isTableHeader = (n: number): boolean => {
    const cur = lines[n];
    const next = lines[n + 1];
    return cur !== undefined && cur.includes('|') && next !== undefined && isTableSeparator(next);
  };

  while (i < lines.length) {
    const line = lines[i] ?? '';

    // Blank line → block break.
    if (line.trim() === '') {
      i += 1;
      continue;
    }

    // Heading.
    const h = /^(#{1,6})\s+(.*)$/.exec(line);
    if (h) {
      blocks.push({ kind: 'h', level: h[1]!.length, text: h[2]!.trim() });
      i += 1;
      continue;
    }

    // Table: a pipe row followed by a separator row.
    if (isTableHeader(i)) {
      const header = splitRow(line);
      const rows: string[][] = [];
      i += 2; // skip header + separator
      let row = lines[i];
      while (row !== undefined && row.includes('|') && row.trim() !== '') {
        rows.push(splitRow(row));
        i += 1;
        row = lines[i];
      }
      blocks.push({ kind: 'table', header, rows });
      continue;
    }

    // Bullet list.
    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = [];
      let item = lines[i];
      while (item !== undefined && /^\s*[-*]\s+/.test(item)) {
        items.push(item.replace(/^\s*[-*]\s+/, '').trim());
        i += 1;
        item = lines[i];
      }
      blocks.push({ kind: 'ul', items });
      continue;
    }

    // Paragraph: gather consecutive non-blank, non-special lines.
    const para: string[] = [];
    let p = lines[i];
    while (
      p !== undefined &&
      p.trim() !== '' &&
      !/^(#{1,6})\s+/.test(p) &&
      !/^\s*[-*]\s+/.test(p) &&
      !isTableHeader(i)
    ) {
      para.push(p.trim());
      i += 1;
      p = lines[i];
    }
    blocks.push({ kind: 'p', text: para.join(' ') });
  }

  return blocks;
}

function Markdown({ source }: { source: string }) {
  const blocks = parseMarkdown(source);
  const headingSize = (level: number): string =>
    ({ 1: '15pt', 2: '12.5pt', 3: '11pt' }[level] ?? '10pt');

  return (
    <div style={{ fontSize: '9.5pt', lineHeight: 1.55, color: 'var(--omega-ink)' }}>
      {blocks.map((b, idx) => {
        if (b.kind === 'h') {
          return (
            <div
              key={idx}
              style={{
                fontSize: headingSize(b.level),
                fontWeight: 700,
                margin: b.level <= 2 ? '20px 0 8px' : '14px 0 6px',
                color: 'var(--omega-ink)',
              }}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: renderInline(b.text) }}
            />
          );
        }
        if (b.kind === 'p') {
          return (
            <p
              key={idx}
              style={{ margin: '8px 0' }}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: renderInline(b.text) }}
            />
          );
        }
        if (b.kind === 'ul') {
          return (
            <ul key={idx} style={{ margin: '8px 0', paddingLeft: 20 }}>
              {b.items.map((it, j) => (
                <li
                  key={j}
                  style={{ margin: '3px 0' }}
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: renderInline(it) }}
                />
              ))}
            </ul>
          );
        }
        // table
        return (
          <div key={idx} style={{ overflowX: 'auto', margin: '12px 0' }}>
            <table style={{ borderCollapse: 'collapse', fontSize: '9pt', width: '100%' }}>
              <thead>
                <tr>
                  {b.header.map((h, j) => (
                    <th
                      key={j}
                      style={{
                        textAlign: 'left',
                        padding: '6px 10px',
                        borderBottom: '2px solid var(--omega-rim, rgba(0,0,0,0.15))',
                        fontWeight: 700,
                      }}
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: renderInline(h) }}
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {b.rows.map((row, r) => (
                  <tr key={r}>
                    {row.map((cell, c) => (
                      <td
                        key={c}
                        style={{
                          padding: '6px 10px',
                          borderBottom: '1px solid var(--omega-rim, rgba(0,0,0,0.08))',
                          verticalAlign: 'top',
                        }}
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: renderInline(cell) }}
                      />
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function OmegaMarketResearchPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const research = useResearch();
  const [activeKey, setActiveKey] = useState<string | null>(null);

  const docs = research.data?.docs ?? [];
  const active = docs.find((d) => d.key === activeKey) ?? docs[0];

  return (
    <div
      className="omega-shell"
      style={{ fontFamily: 'var(--omega-sans)', color: 'var(--omega-ink)', padding: '16px 20px' }}
    >
      <Breadcrumbs crumbs={[{ label: 'OMEGA', to: '/omega' }, { label: 'Market Research' }]} />

      <div style={{ marginTop: 8, marginBottom: 6 }}>
        <h1
          style={{
            margin: 0,
            fontSize: '12pt',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          }}
        >
          Market Research
        </h1>
        <div style={{ fontSize: '9pt', color: 'var(--omega-ink-muted)', marginTop: 4 }}>
          All product and market research — Monitor, Guardian Runtime / Loop, and the proof + revenue model — in one place.
        </div>
      </div>

      {research.isPending && (
        <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt', margin: '14px 0' }}>
          Loading…
        </div>
      )}
      {research.isError && (
        <div style={{ color: '#dc2626', fontSize: '9pt', margin: '14px 0' }}>
          Failed to load market research.
        </div>
      )}

      {!research.isPending && !research.isError && docs.length > 0 && (
        <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start', flexWrap: 'wrap', marginTop: 14 }}>
          {/* Doc switcher */}
          <nav style={{ flex: '0 0 240px', minWidth: 200 }}>
            {docs.map((d) => {
              const isActive = active && d.key === active.key;
              return (
                <button
                  key={d.key}
                  type="button"
                  onClick={() => setActiveKey(d.key)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '9px 12px',
                    marginBottom: 6,
                    fontSize: '9pt',
                    lineHeight: 1.35,
                    borderRadius: 6,
                    cursor: 'pointer',
                    border: isActive
                      ? '1px solid var(--omega-accent, #2563eb)'
                      : '1px solid var(--omega-rim, rgba(0,0,0,0.1))',
                    background: isActive
                      ? 'var(--omega-accent, #2563eb)'
                      : 'var(--omega-surface, rgba(0,0,0,0.02))',
                    color: isActive ? '#ffffff' : 'var(--omega-ink)',
                    fontWeight: isActive ? 700 : 500,
                  }}
                >
                  {d.title}
                </button>
              );
            })}
          </nav>

          {/* Active doc */}
          <article
            style={{
              flex: '1 1 480px',
              minWidth: 0,
              background: 'var(--omega-surface, rgba(0,0,0,0.02))',
              border: '1px solid var(--omega-rim, rgba(0,0,0,0.08))',
              borderRadius: 8,
              padding: '18px 22px',
            }}
          >
            {active && <Markdown source={active.markdown} />}
          </article>
        </div>
      )}

      <div style={{ marginTop: 18 }}>
        <Link to="/omega" style={{ color: '#2563eb', fontSize: '8.5pt' }}>
          ← OMEGA
        </Link>
      </div>
    </div>
  );
}
